import React, { useMemo, useState } from "react";
import {
  Box, Grid, Typography, Chip, Button, IconButton, Avatar, Divider, LinearProgress,
  Menu, MenuItem, Skeleton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  alpha,
} from "@mui/material";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlaceIcon from "@mui/icons-material/Place";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useGetClassDetailQuery, useDeleteAssignClassMutation } from "../../../Api/attoDeskApi";
import useUser from "../../../Core/hook/useUser";
import { useNotifier } from "../../../Core/Notifier";
import { StaffProfileLink, StudentProfileLink } from "../../../Components/ProfileLinks";
import SendMessagePanel from "../../../Components/SendMessagePanel";
import { formatDaysList, gradeSubjectsSummaryLine, capitalise } from "./classDetailHeaderHelpers";
import { GradeSubjectsSummary, FlatSubjectsSummary } from "../../../Components/Subjects/SubjectCollection";
// Pattern-level dialogs reused from Staff View → Class Schedules. They own the
// pattern-level APIs (/class-schedules/{scheduleId}/release-student,
// /switch-staff) and have the correct safety copy about history preservation.
// See the 2026-10-08 class-detail audit §2 for the full reuse rationale.
import ReleaseStudentDialog from "../../Staff/View/Component/ClassScheduleActions/ReleaseStudentDialog";
import SwitchStaffDialog from "../../Staff/View/Component/ClassScheduleActions/SwitchStaffDialog";
import DuplicateDialog from "../../ClassSchedule/List/DuplicateDialog";
import type { SchedulePattern } from "../../Staff/View/Component/ClassSchedulesTab";

// Re-export pure derivation helpers so existing importers of ClassDetailPage
// keep working; they live in a React-free module for unit tests.
export { formatDaysList, gradeSubjectsSummaryLine, capitalise } from "./classDetailHeaderHelpers";

const fmtDate = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY") : "—");
const fmtDateFull = (d?: string) => (d ? dayjs(d).format("dddd, MMMM D, YYYY") : "—");
const fmtDateTime = (d?: string) => (d ? dayjs(d).format("MMM D, YYYY [at] h:mm A") : "—");
const fmtTime = (t?: string) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const dt = new Date(); dt.setHours(h, m || 0, 0, 0);
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).format(dt);
};
const timeRange = (s?: string, e?: string) => [fmtTime(s), fmtTime(e)].filter(Boolean).join(" – ");

const STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  scheduled: { bg: "#eff6ff", fg: "#2563eb" },
  completed: { bg: "#f0fdf4", fg: "#16a34a" },
  cancelled: { bg: "#fef2f2", fg: "#dc2626" },
  "created class": { bg: "#eff6ff", fg: "#2563eb" },
};
const statusColor = (s?: string) => STATUS_COLORS[(s || "").toLowerCase()] || { bg: "#f1f5f9", fg: "#475569" };

// ── Small building blocks ──
const Section: React.FC<{ title: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, action, children }) => (
  <Box sx={{ bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(15,23,42,0.06)", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", p: { xs: 2, md: 2.5 }, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </Box>
);

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", mt: 0.25 }}>{value ?? "—"}</Typography>
  </Box>
);

const HeaderChip: React.FC<{ icon?: React.ReactNode; label: string; color?: string }> = ({ icon, label, color = "#475569" }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1.1, py: 0.4, borderRadius: "8px", bgcolor: alpha(color, 0.08), color }}>
    {icon}
    <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>{label}</Typography>
  </Box>
);

const EmptyState: React.FC<{ icon: React.ReactNode; text: string; sub?: string; action?: React.ReactNode }> = ({ icon, text, sub, action }) => (
  <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
    <Box sx={{ color: "#cbd5e1", "& svg": { fontSize: 36 } }}>{icon}</Box>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#64748b", mt: 1 }}>{text}</Typography>
    {sub && <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.5 }}>{sub}</Typography>}
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>
);

type ClassDetailPageProps = {
  /** Route param name to read the class id from. Defaults to "id". */
  paramName?: string;
  /** Fallback path used when history has no previous entry. */
  fallbackBackUrl?: string;
};

const ClassDetailPage: React.FC<ClassDetailPageProps> = ({
  paramName = "id",
  fallbackBackUrl,
}) => {
  const params = useParams<Record<string, string>>();
  const id = params[paramName];
  const [searchParams] = useSearchParams();
  const occurrenceId = searchParams.get("occurrence_id") || undefined;
  // Route B and other callers pass a `return_url` so the back arrow honours the
  // origin tab (e.g. the ClassSchedules tab inside team-history).
  const returnUrl = searchParams.get("return_url") || undefined;
  // Origin tag routed to the Add Student page so the forward CTA
  // (Continue to Assign Class) can, in turn, forward the same source
  // to the final Assign Class page for post-assignment navigation.
  const originSource = searchParams.get("source") || undefined;
  // Add Student URL — carries the caller's return_url + source so the
  // Add-Student → Continue-to-Assign → success chain lands back where
  // this page was itself opened from (typically Class Schedules).
  const addStudentUrl = (() => {
    const p = new URLSearchParams({ classId: String(id ?? "") });
    // Default the round-trip target to *this* class detail page so the
    // class-first flow (Add Student → Continue → Billing → success)
    // lands back here. Callers that arrived with their own return_url
    // (e.g. a tab embedding this page) keep theirs.
    const outgoingReturn = returnUrl ?? (id ? `/class-schedules/${id}` : undefined);
    if (outgoingReturn) p.set("return_url", outgoingReturn);
    if (originSource) p.set("source", originSource);
    return `/assign/classes/create/student/0?${p.toString()}`;
  })();
  const navigate = useNavigate();
  const location = useLocation();
  // Callers that reach this page via a return_url should keep the current URL
  // (including occurrence_id) as *their* return target — makes the back-chain
  // symmetrical.
  const currentAsReturn = encodeURIComponent(location.pathname + location.search);
  const goBack = () => {
    if (returnUrl) navigate(returnUrl);
    else if (window.history.length > 1) navigate(-1);
    else navigate(fallbackBackUrl ?? "/class-schedule/list");
  };
  // Edit target is the shared /classes/:id/edit page (Pages/Classes/Edit
  // renders <ClassForm mode="edit" />, the same premium form used by
  // Create). Legacy `/class-schedules/:id/edit` is a separate simplified
  // UI that the app-wide spec now forbids as an entry point. We forward
  // the current URL as the edit page's return_url so its Cancel/Save
  // arrive here again.
  const goEdit = () => navigate(`/classes/${id}/edit?return_url=${currentAsReturn}`);
  // View Sessions → the dated Class Sessions list, pinned to THIS class only.
  // We pass only `class_id` (and `return_url`) — adding staff_id broadened
  // the result to every other class the same teacher owned, and student_id
  // dropped future sessions where the roster changes. The backend accepts
  // `class_id` and filters at the DB level; the list page's URL hydration
  // seeds the "Single series" chip from the same param so a direct link /
  // refresh keeps the scope.
  const goViewSessions = () => {
    const p = new URLSearchParams();
    if (id) p.set("class_id", String(id));
    p.set("return_url", location.pathname + location.search);
    navigate(`/class-schedule/list?${p.toString()}`);
  };
  const { canEdit, canCreate, userHasRight } = useUser();
  const { showMessage, showErrorMessage } = useNotifier();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteClass, { isLoading: isDeleting }] = useDeleteAssignClassMutation();

  // Per-student row kebab menu + preselected id handed to ReleaseStudentDialog.
  const [studentMenu, setStudentMenu] = useState<{ anchor: HTMLElement; studentId: number } | null>(null);
  const [releaseStudentId, setReleaseStudentId] = useState<number | null>(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [switchStaffOpen, setSwitchStaffOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);

  const { data, isLoading, isFetching, isError, refetch } = useGetClassDetailQuery(
    { classId: id as string, occurrenceId },
    { skip: !id }
  );
  const d: any = data?.data;
  const cls = d?.class;
  const isPeriod = /period/i.test(cls?.class_type || "");

  const canEditClass = canEdit("Classes");
  const canDeleteClass = userHasRight("Classes_Delete");

  const handleDelete = async () => {
    try {
      const res = await deleteClass(String(id)).unwrap();
      if (res.status) { showMessage("Class deleted."); navigate("/class-schedule/list"); }
      else showErrorMessage(res.message || "Delete failed.");
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Delete failed.");
    }
    setConfirmDelete(false);
  };

  // ── Loading skeleton ──
  // Only show the full-page skeleton on the FIRST load (no cached data). When
  // the user returns from Create New Class the detail cache is still present
  // (keepUnusedDataFor=300 on getClassDetail), so `data` is truthy and we
  // render the real content immediately. If the cache was marked stale by a
  // mutation, `isFetching` fires a quiet background refetch — the subtle
  // "Refreshing…" strip below indicates that without blanking the page.
  if (isLoading && !data) {
    return (
      <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", p: { xs: 2, md: 3 } }}>
        <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: "14px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}><Skeleton variant="rounded" height={500} sx={{ borderRadius: "14px" }} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={500} sx={{ borderRadius: "14px" }} /></Grid>
        </Grid>
      </Box>
    );
  }

  // ── Error / not-found ──
  if (isError || !cls) {
    return (
      <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
        <Box sx={{ textAlign: "center", bgcolor: "#fff", p: 5, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)" }}>
          <ErrorOutlineIcon sx={{ fontSize: 44, color: "#ef4444" }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, mt: 1 }}>Class not found</Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
            This class may have been deleted or you don’t have access to it.
          </Typography>
          <Box sx={{ mt: 2.5, display: "flex", gap: 1, justifyContent: "center" }}>
            <Button variant="outlined" onClick={() => refetch()} sx={{ textTransform: "none", borderRadius: "9px" }}>Retry</Button>
            <Button variant="contained" onClick={() => navigate("/class-schedule/list")} sx={{ textTransform: "none", borderRadius: "9px" }}>Back to Class Sessions</Button>
          </Box>
        </Box>
      </Box>
    );
  }

  const en = d.enrollment;
  const cap = en.capacity || 0;
  const fillPct = cap > 0 ? Math.min(100, Math.round((en.enrolled / cap) * 100)) : 0;
  const enrollFg = en.enrolled === 0 ? "#dc2626" : en.enrolled >= cap && cap > 0 ? "#16a34a" : "#d97706";
  const sc = statusColor(cls.status);
  const modeIcon = cls.is_online ? <LaptopMacIcon sx={{ fontSize: 14 }} /> : <PlaceIcon sx={{ fontSize: 14 }} />;

  // ── Pattern projection for pattern-level dialogs ─────────────────────
  // The Staff-View dialogs (ReleaseStudentDialog, SwitchStaffDialog) take a
  // SchedulePattern and call /class-schedules/{schedule_id}/*. The class
  // detail payload now carries schedule_id (primary) + schedule_ids (all).
  // Phase 3B v1 uses the primary pattern only; multi-pattern support is a
  // follow-up (see ClassDetailController comment on $dayRows ordering).
  const scheduleId: number | null = (d?.schedule?.schedule_id ?? null) as number | null;
  const hasMultiplePatterns = Array.isArray(d?.schedule?.schedule_ids)
    && (d.schedule.schedule_ids.length > 1);
  const pattern: SchedulePattern | null = scheduleId ? {
    class_id: Number(cls.id),
    schedule_id: Number(scheduleId),
    staff: d.staff ? { id: Number(d.staff.id), name: String(d.staff.name || "") } : null,
    day: (d.schedule?.days && d.schedule.days[0]) || "",
    start_time: cls.start_time || "",
    end_time: cls.end_time || "",
    class_type: cls.class_type || null,
    program: cls.program_type || null,
    study_mode: cls.study_mode || null,
    location: cls.location_name
      ? { id: Number(cls.location_id || 0), name: String(cls.location_name) }
      : null,
    grade_subjects: Array.isArray(d?.class?.grade_subjects)
      ? d.class.grade_subjects.map((g: any) => ({
          grade_id: Number(g.grade_id || 0),
          grade: String(g.grade || ""),
          subjects: Array.isArray(g.subjects)
            ? g.subjects.map((s: any) =>
                typeof s === "string"
                  ? { id: 0, name: s }
                  : { id: Number(s.id || 0), name: String(s.name || "") }
              )
            : [],
        }))
      : [],
    capacity: Number(cap || 0),
    enrolled: Number(en.enrolled || 0),
    available: en.available ?? null,
    students: Array.isArray(d.students)
      ? d.students.map((s: any) => ({
          id: Number(s.id),
          name: String(s.name || ""),
          grade: s.grade ?? null,
          image: s.image ?? null,
        }))
      : [],
    recurring: true,
    status: String(cls.status || "scheduled"),
    next_occurrence_id: d.schedule?.next_class?.occurrence_id ?? null,
    next_occurrence_date: d.schedule?.next_class?.date ?? null,
    // Series bounds: period-based classes carry explicit from/to; recurring
    // classes fall back to earliest generated occurrence for start and to
    // schedule_end_date for end (null = ongoing / no end date).
    series_start_date: isPeriod
      ? (cls.period_based_from_date ?? null)
      : (d.schedule?.series_start_date ?? null),
    series_end_date: isPeriod
      ? (cls.period_based_to_date ?? null)
      : (cls.schedule_end_date ?? null),
  } : null;

  // ── Duplicate dialog row synthesis ───────────────────────────────────
  // Phase 3B fix: Duplicate Class previously navigated to the Create route
  // (effectively creating a new class, not duplicating). Now wires to the
  // real DuplicateDialog + POST /api/class-schedule/duplicate. The dialog
  // supports scope='occurrence' (needs occurrenceId) and scope='series'
  // (needs classId). For class-detail entry, series is the natural default.
  const duplicateRow = {
    id: d.schedule?.next_class?.occurrence_id ?? null,
    classId: cls.id,
    className: (gradeSubjectsSummaryLine(d?.class?.grade_subjects, cls) || cls.title || "Class"),
    staffId: cls.staff_id ?? d?.staff?.id ?? null,
    staffName: cls.staff_name ?? d?.staff?.name ?? "Unassigned",
    classMode: cls.mode_label || (cls.is_online ? "Online" : "In-person"),
    scheduledDate: d.schedule?.next_class?.date ?? null,
    startTime: cls.start_time ?? null,
    endTime: cls.end_time ?? null,
    isRecurring: true,
  };

  // After a successful release / switch / duplicate, RTK Query cache
  // invalidation (classSchedule, class, team, attendanceRecord) refreshes
  // the detail payload — no manual refetch needed. We keep an explicit
  // refetch() available in case a mutation later invalidates nothing.
  const onPatternActionDone = () => { setReleaseOpen(false); setSwitchStaffOpen(false); refetch(); };

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", pb: { xs: 10, md: 3 } }}>
      {/* Subtle background-refresh indicator for the "cached detail on return"
          case — the real content below stays visible; this strip signals that
          a quiet refetch is in flight (e.g. after a mutation marked the
          cached data stale). First-ever load uses the full skeleton above. */}
      {isFetching && data && (
        <LinearProgress sx={{ height: 2, "& .MuiLinearProgress-bar": { transition: "none" } }} />
      )}
      {/* ── Header — staff-first hero ─────────────────────────────────
          The old header led with the flattened class title ("Grade 10
          Maths"), which hid the staff identity in a small chip. The new
          hero puts the teaching staff first (large avatar + big name),
          reduces the class label to a subtitle, and adds a schedule
          summary + badge row underneath. Uses a soft brand gradient
          without depending on a per-location colour prop. */}
      <Box sx={{
        bgcolor: "#fff",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
        px: { xs: 2, md: 3 }, py: { xs: 2.25, md: 2.75 },
        background: `linear-gradient(135deg, ${alpha("#16a34a", 0.06)} 0%, ${alpha("#0ea5e9", 0.04)} 60%, #fff 100%)`,
      }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 1.75, minWidth: 0, alignItems: "flex-start" }}>
            <IconButton onClick={goBack} size="small" sx={{ mt: 0.5, bgcolor: "rgba(15,23,42,0.05)", flexShrink: 0 }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
            <Avatar sx={{
              width: { xs: 56, md: 64 }, height: { xs: 56, md: 64 },
              fontSize: { xs: 20, md: 22 }, fontWeight: 800,
              bgcolor: alpha("#16a34a", 0.15), color: "#16a34a",
              border: `2px solid ${alpha("#16a34a", 0.25)}`,
              boxShadow: `0 6px 18px -8px ${alpha("#16a34a", 0.5)}`,
              flexShrink: 0, mt: 0.25,
            }} aria-label={cls.staff_name || "Staff"}>
              {(cls.staff_name || "?").split(" ").filter(Boolean).slice(0, 2).map((n: string) => n[0]?.toUpperCase()).join("") || "?"}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              {/* 1. Staff name — largest line, dominant. Clickable → the
                     staff profile. Fallback: when the payload doesn't
                     carry staff_id, we render plain text (never route to
                     `/team-history/undefined`). */}
              <StaffProfileLink
                id={cls.staff_id ?? (d as any)?.staff?.id}
                name={cls.staff_name || "Unassigned"}
                sx={{ display: "block", fontSize: { xs: 22, md: 26 }, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1, color: "#0f172a", wordBreak: "break-word" }}
              >
                {cls.staff_name || "Unassigned"}
              </StaffProfileLink>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, mt: 0.15 }}>
                Teacher
              </Typography>
              {/* 2. Class identity. For broad multi-grade classes the
                     shared GradeSubjectsSummary (hero variant) collapses
                     to "N Grades · N Subjects [View Grades & Subjects]"
                     so the hero never grows into a giant paragraph. For
                     small classes we keep the existing readable
                     `gradeSubjectsSummaryLine` output. */}
              {(() => {
                const gs = d?.class?.grade_subjects;
                const gsCount = Array.isArray(gs) ? gs.length : 0;
                const summaryLine = gradeSubjectsSummaryLine(gs, cls) || cls.title;
                // Threshold: >= 3 grades OR summary line looks paragraph-length.
                const useCompact = gsCount >= 3 || (summaryLine && summaryLine.length > 90);
                if (useCompact && gsCount > 0) {
                  return (
                    <Box sx={{ mt: 1 }}>
                      <GradeSubjectsSummary
                        groups={gs as any}
                        variant="hero"
                        modalTitle="Subjects by Grade"
                        viewButtonLabel="View Grades & Subjects"
                      />
                    </Box>
                  );
                }
                return (
                  <Typography sx={{ fontSize: { xs: 13.5, md: 14.5 }, fontWeight: 700, color: "#0f172a", mt: 1 }}>
                    {summaryLine}
                  </Typography>
                );
              })()}
              {/* 3. Schedule summary — next-class date is now the
                     visual anchor (big bold navy), with the time row
                     underneath in muted grey. Previous compact layout
                     showed only tiny time text ("12:00 AM – 2:00 AM")
                     and buried the date entirely. */}
              {d?.schedule?.next_class?.date ? (
                <Box sx={{ mt: 0.75 }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#1b3a6b", lineHeight: 1.2 }}>
                    {fmtDateFull(d.schedule.next_class.date)}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.25 }}>
                    🕐 {timeRange(cls.start_time, cls.end_time) || "Time not set"}
                    {formatDaysList(d?.schedule?.days) ? (
                      <> · Repeats {formatDaysList(d.schedule.days)}</>
                    ) : null}
                  </Typography>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.25 }}>
                  {formatDaysList(d?.schedule?.days)}
                  {timeRange(cls.start_time, cls.end_time) ? (
                    <>
                      {" · "}
                      <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                        {timeRange(cls.start_time, cls.end_time)}
                      </Box>
                    </>
                  ) : null}
                </Typography>
              )}
              {/* 4. Badge row. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 1.25 }}>
                <Chip label={cls.status || "Scheduled"} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: sc.bg, color: sc.fg, textTransform: "capitalize" }} />
                <HeaderChip icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 14 }} />} label={cls.class_type || "Regular"} color="#7c3aed" />
                <HeaderChip icon={modeIcon} label={cls.mode_label} color={cls.is_online ? "#2563eb" : "#16a34a"} />
                {!cls.is_online && cls.location_name && <HeaderChip icon={<PlaceIcon sx={{ fontSize: 14 }} />} label={cls.location_name} color="#64748b" />}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            {canEditClass && (
              <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} onClick={() => goEdit()} sx={{ textTransform: "none", fontWeight: 600, borderRadius: "9px" }}>Edit Class</Button>
            )}
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ bgcolor: "rgba(0,0,0,0.04)" }}><MoreVertIcon sx={{ fontSize: 18 }} /></IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { setMenuAnchor(null); goViewSessions(); }}>View Sessions</MenuItem>
              {canCreate("Classes") && <MenuItem onClick={() => { setMenuAnchor(null); navigate("/assign/classes/create"); }}>Create New Class</MenuItem>}
              {canCreate("Classes") && <MenuItem onClick={() => { setMenuAnchor(null); setDuplicateOpen(true); }}>Duplicate Class</MenuItem>}
              {canDeleteClass && <MenuItem onClick={() => { setMenuAnchor(null); setConfirmDelete(true); }} sx={{ color: "#dc2626" }}>Delete Class</MenuItem>}
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* ── Two-column body ── */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
        <Grid container spacing={2}>
          {/* LEFT (70%) */}
          <Grid item xs={12} md={8}>
            {/* Subjects by Grade — routed through the shared
                GradeSubjectsSummary primitive so the section stays
                compact regardless of class breadth. Small classes
                render inline rows; broad multi-grade classes collapse
                to counts + 3-grade preview + "View All Grades &
                Subjects" that opens the shared SubjectDetailsDialog
                (search, per-grade expand, all mappings visible). No
                inline `.map` chip wall is rendered on the page any
                more, even for a 12-grade × 10-subject class. */}
            <Section title="Subjects by Grade">
              {(() => {
                const groups: Array<{ grade_id?: number; grade: string; subjects: Array<{ id?: number; name: string } | string> }> =
                  Array.isArray(d?.class?.grade_subjects) && d.class.grade_subjects.length > 0
                    ? d.class.grade_subjects
                    : (cls.grades || []).map((g: string) => ({
                        grade: g,
                        subjects: (cls.subjects || []) as string[],
                      }));
                return (
                  <GradeSubjectsSummary
                    groups={groups as any}
                    modalTitle="Subjects by Grade"
                  />
                );
              })()}
            </Section>

            {/* Class overview
                ─────────────────────────────────
                The old grid rendered "Subject" and "Grade" as disconnected
                columns (`Subject: English` / `Grade: Grade 1`), which
                misrepresented multi-grade classes and duplicated data the
                "Subjects by Grade" card above already shows canonically.
                Those two fields are gone; everything else is retained and
                displayed as complementary teaching / scheduling / capacity
                metadata. Dashes for missing values are replaced with
                "Not Set" so admins see intent instead of a mysterious
                em-dash. Program type and status are Title-Cased. */}
            <Section title="Class Overview">
              <Grid container spacing={2}>
                {[
                  ["Staff", cls.staff_name || "Unassigned"],
                  ["Class Type", capitalise(cls.class_type)],
                  ["Enrollment Type", capitalise(cls.program_type)],
                  ["Mode", cls.mode_label || "Not Set"],
                  ["Location", cls.location_name || "Not Set"],
                  // Start/End Time cells lead with the next-class DATE
                  // so admins scanning the Overview grid see when the
                  // class runs, not just an unmoored clock reading.
                  ["Start Time", (
                    <Box>
                      <Box sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {d.schedule?.next_class?.date ? fmtDate(d.schedule.next_class.date) : "Not Set"}
                      </Box>
                      <Box sx={{ color: "#64748b", fontSize: "0.9em" }}>{fmtTime(cls.start_time) || "—"}</Box>
                    </Box>
                  )],
                  ["End Time", (
                    <Box>
                      <Box sx={{ fontWeight: 600, color: "#0f172a" }}>
                        {d.schedule?.next_class?.date ? fmtDate(d.schedule.next_class.date) : "Not Set"}
                      </Box>
                      <Box sx={{ color: "#64748b", fontSize: "0.9em" }}>{fmtTime(cls.end_time) || "—"}</Box>
                    </Box>
                  )],
                  ["Next Class", d.schedule?.next_class ? fmtDate(d.schedule.next_class.date) : "Not Set"],
                  ["Capacity", cap || "Not Set"],
                  ["Enrolled", en.enrolled],
                  ["Available", en.available ?? (cap ? cap - en.enrolled : "Not Set")],
                  ["Status", <Box component="span" sx={{ color: sc.fg, textTransform: "capitalize", fontWeight: 700 }}>{capitalise(cls.status) || "Scheduled"}</Box>],
                ].map(([label, value], i) => (
                  <Grid item xs={6} sm={4} md={3} key={i}><Field label={label as string} value={value as React.ReactNode} /></Grid>
                ))}
              </Grid>
            </Section>

            {/* Students */}
            <Section
              title={`Students (${d.students.length})`}
              action={canEditClass && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<PersonAddAltIcon sx={{ fontSize: 15 }} />} onClick={() => navigate(addStudentUrl)} sx={{ textTransform: "none", borderRadius: "8px", fontSize: 12 }}>Add Student</Button>
                </Box>
              )}
            >
              {/* Subtle guidance so staff know Release ≠ Delete and that
                  historical attendance/invoicing is preserved. Multi-pattern
                  classes get an extra line clarifying the primary-pattern
                  scope — see audit §G. */}
              <Typography sx={{ fontSize: 11.5, color: "#64748b", mb: 1.25 }}>
                Release a student to remove them from this class while keeping past attendance and history.
                {hasMultiplePatterns && (
                  <> Applies to the primary schedule pattern.</>
                )}
              </Typography>
              {d.students.length === 0 ? (
                <EmptyState
                  icon={<GroupsOutlinedIcon />}
                  text="No students assigned to this class."
                  sub="Assign a student to start generating attendance sessions."
                  action={canEditClass && <Button variant="contained" size="small" startIcon={<PersonAddAltIcon sx={{ fontSize: 15 }} />} onClick={() => navigate(addStudentUrl)} sx={{ textTransform: "none", borderRadius: "9px" }}>Assign Students</Button>}
                />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {d.students.map((s: any) => (
                    <Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1, borderRadius: "10px", border: "1px solid rgba(15,23,42,0.05)", "&:hover": { bgcolor: "#f8fafc" } }}>
                      <Avatar src={s.image || undefined} sx={{ width: 34, height: 34, fontSize: 13 }}>{(s.name || "?").charAt(0)}</Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        {/* Student name is the primary way to open the
                            profile — the tiny "View" text link on the right
                            is redundant now, but kept so existing users
                            still recognise the row. */}
                        <StudentProfileLink id={s.id} name={s.name} sx={{ display: "block", fontSize: 13, fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {s.name}
                        </StudentProfileLink>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
                          {s.reference_id ? `#${s.reference_id}` : ""}{s.grade ? ` · ${s.grade}` : ""}
                        </Typography>
                      </Box>
                      <Chip label={s.status || "active"} size="small" sx={{ height: 20, fontSize: 10, textTransform: "capitalize", bgcolor: "#f0fdf4", color: "#16a34a" }} />
                      <Button size="small" onClick={() => navigate(`/student-history/${s.id}`)} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }}>View</Button>
                      {/* Per-row kebab. Only renders when the user can edit
                          the class AND we have a schedule_id to drive the
                          pattern-level Release endpoint. Without a schedule
                          pattern the dialog has nothing to call, so we hide
                          the "Release From Class" option rather than ship a
                          broken menu entry. */}
                      {canEditClass && scheduleId !== null && (
                        <>
                          <IconButton
                            size="small"
                            aria-label={`Actions for ${s.name || "student"}`}
                            onClick={(e) => setStudentMenu({ anchor: e.currentTarget, studentId: Number(s.id) })}
                            sx={{ ml: 0.25 }}
                          >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Section>

            {/* Schedule */}
            <Section title="Schedule" action={<Button size="small" onClick={goViewSessions} sx={{ textTransform: "none", fontSize: 12 }}>View all sessions</Button>}>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={6} sm={4}><Field label="Repeats" value={d.schedule.days?.join(", ") || "—"} /></Grid>
                <Grid item xs={6} sm={4}><Field label="Time" value={timeRange(d.schedule.start_time, d.schedule.end_time)} /></Grid>
                {isPeriod ? (
                  <>
                    <Grid item xs={6} sm={4}><Field label="Start Date" value={fmtDate(cls.period_based_from_date)} /></Grid>
                    <Grid item xs={6} sm={4}><Field label="End Date" value={fmtDate(cls.period_based_to_date)} /></Grid>
                  </>
                ) : (
                  <Grid item xs={6} sm={4}><Field label="Generated Through" value={fmtDate(d.schedule.generated_through)} /></Grid>
                )}
                <Grid item xs={6} sm={4}><Field label="Upcoming Sessions" value={d.schedule.upcoming_count} /></Grid>
                <Grid item xs={6} sm={4}><Field label="Next Class" value={d.schedule.next_class ? fmtDate(d.schedule.next_class.date) : "—"} /></Grid>
              </Grid>
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#64748b", mb: 1 }}>NEXT 5 SESSIONS</Typography>
              {d.schedule.upcoming.length === 0 ? (
                <EmptyState icon={<CalendarMonthOutlinedIcon />} text="No upcoming sessions." />
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {d.schedule.upcoming.map((o: any) => (
                    <Box key={o.occurrence_id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1, borderRadius: "8px", bgcolor: "#f8fafc" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: o.status === "cancelled" ? "#ef4444" : "#2563eb" }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#334155" }}>{o.day}, {fmtDate(o.date)}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>{timeRange(o.start_time, o.end_time)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Section>

            {/* Attendance */}
            <Section
              title="Attendance"
              action={d.attendance && d.attendance.total_students > 0 && (
                <Button size="small" variant="outlined" startIcon={<HowToRegOutlinedIcon sx={{ fontSize: 15 }} />} onClick={() => d.attendance?.occurrence_id && navigate(`/class-sessions/${d.attendance.occurrence_id}/attendance?return_url=/class-schedules/${id}`)} sx={{ textTransform: "none", borderRadius: "8px", fontSize: 12 }}>Mark Attendance</Button>
              )}
            >
              {/* Attendance empty-state disambiguation ─────────────────
                  Old behaviour: showed "No students are enrolled for this
                  session" whenever d.attendance.total_students === 0, so
                  a class with one student but no attendance-generated
                  occurrence yet appeared to have zero students — clashing
                  with "Students (1)" and the Enrollment sidebar. Now we
                  distinguish three states:
                    • no attendance payload at all → nothing to show
                    • class has students, session has none yet → "Attendance
                      has not been recorded for this session yet"
                    • class has no students → the original empty state
                  Still gates on d.students so the summary tiles never
                  render zeros against a real roster. */}
              {(() => {
                const classHasStudents = (d?.students?.length ?? 0) > 0;
                if (!d.attendance) {
                  return (
                    <EmptyState
                      icon={<HowToRegOutlinedIcon />}
                      text={classHasStudents
                        ? "Attendance has not been recorded for this session yet."
                        : "No students are assigned to this class."}
                      sub={classHasStudents
                        ? "Mark attendance once a session begins to see present / absent / late counts."
                        : "Attendance becomes available once a student is assigned to the class."}
                    />
                  );
                }
                if (d.attendance.total_students === 0) {
                  return (
                    <EmptyState
                      icon={<HowToRegOutlinedIcon />}
                      text={classHasStudents
                        ? "Attendance has not been recorded for this session yet."
                        : "No students are assigned to this class."}
                      sub={classHasStudents
                        ? `The next session on ${fmtDate(d.attendance.date)} will be attended by the students shown above.`
                        : "Attendance becomes available once a student is assigned to the class."}
                    />
                  );
                }
                return null;
              })()}
              {d.attendance && d.attendance.total_students > 0 && (
                <>
                  <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1.5 }}>
                    Session on <b>{fmtDate(d.attendance.date)}</b> · {timeRange(d.attendance.start_time, d.attendance.end_time)}
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(5, 1fr)" }, gap: 1.5 }}>
                    {[
                      ["Total", d.attendance.total_students, "#475569"],
                      ["Present", d.attendance.present, "#16a34a"],
                      ["Absent", d.attendance.absent, "#dc2626"],
                      ["Late", d.attendance.late, "#d97706"],
                      ["Not Marked", d.attendance.not_marked, "#94a3b8"],
                    ].map(([label, value, color], i) => (
                      <Box key={i} sx={{ textAlign: "center", p: 1.25, borderRadius: "10px", bgcolor: alpha(color as string, 0.06) }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: color as string }}>{value as number}</Typography>
                        <Typography sx={{ fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>{label as string}</Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Section>

            {/* Staff Communication panel moved to the attendance flow
                only — the Send Message button on the attendance page
                opens the same panel there, keeping messaging tied to
                a live session instead of duplicated on this overview. */}

            {/* Notes */}
            <Section title="Notes">
              {cls.notes ? (
                <Typography sx={{ fontSize: 13, color: "#334155", whiteSpace: "pre-wrap" }}>{cls.notes}</Typography>
              ) : (
                <EmptyState icon={<EventNoteOutlinedIcon />} text="No notes for this class yet." />
              )}
            </Section>

            {/* Activity timeline */}
            <Section title="Activity">
              {d.activity.length === 0 ? (
                <EmptyState icon={<EventNoteOutlinedIcon />} text="No activity recorded yet." />
              ) : (
                <Box sx={{ position: "relative", pl: 2 }}>
                  <Box sx={{ position: "absolute", left: 5, top: 4, bottom: 4, width: "2px", bgcolor: "#e2e8f0" }} />
                  {d.activity.map((a: any, i: number) => (
                    <Box key={i} sx={{ position: "relative", pb: 2 }}>
                      <Box sx={{ position: "absolute", left: -16, top: 3, width: 10, height: 10, borderRadius: "50%", bgcolor: "#2563eb", border: "2px solid #fff", boxShadow: "0 0 0 2px #e2e8f0" }} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#1e293b" }}>{a.action}</Typography>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
                        {a.user ? `${a.user} · ` : ""}{fmtDateTime(a.datetime)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Section>
          </Grid>

          {/* RIGHT (30%) */}
          <Grid item xs={12} md={4}>
            {/* Quick actions */}
            <Section title="Quick Actions">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {canEditClass && (
                  <ActionBtn
                    icon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                    label="Edit Class"
                    // Editing a legacy-imported class promotes it to a live
                    // template on save (see classesAssignController::update
                    // around line 1864). Keep the entry point open so the
                    // user who wants to re-adopt the template still can,
                    // but surface the consequence in the tooltip so it's a
                    // conscious choice rather than a surprise.
                    tip={cls.is_legacy_import ? "Editing a historical imported class promotes it to a live template." : ""}
                    onClick={() => goEdit()}
                  />
                )}
                {canEditClass && (
                  <ActionBtn
                    icon={<PersonAddAltIcon sx={{ fontSize: 16 }} />}
                    label="Add Student"
                    // Legacy-imported classes are frozen snapshots:
                    // ClassOccurrenceService::generate() refuses to run on
                    // them, so there is no path to produce future
                    // occurrences for a newly assigned student. The server
                    // enforces this via AssignmentBillingService; the UI
                    // disables the entry point so the user never reaches
                    // the billing form only to hit a 422 at save.
                    disabled={!!cls.is_legacy_import}
                    tip={cls.is_legacy_import ? "This class was imported as historical data. Use Duplicate Class to create a live schedule, then add students to the duplicate." : ""}
                    onClick={() => navigate(addStudentUrl)}
                  />
                )}
                <ActionBtn icon={<EventNoteOutlinedIcon sx={{ fontSize: 16 }} />} label="View Sessions" tip="View dated sessions for this class" onClick={goViewSessions} />
                <ActionBtn icon={<HowToRegOutlinedIcon sx={{ fontSize: 16 }} />} label="Mark Attendance" disabled={!d.attendance || d.attendance.total_students === 0} tip={!d.attendance || d.attendance.total_students === 0 ? "No students enrolled for the next session" : ""} onClick={() => d.attendance?.occurrence_id && navigate(`/class-sessions/${d.attendance.occurrence_id}/attendance?return_url=/class-schedules/${id}`)} />
                {canEditClass && (
                  <ActionBtn
                    icon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
                    label="Switch Staff"
                    // When no pattern is in scope (an edge case for classes
                    // with no assign_classes_schedules row), the dialog has
                    // nothing to post to. Disable instead of ship a 404.
                    disabled={scheduleId === null}
                    tip={scheduleId === null ? "No active schedule pattern on this class." : ""}
                    onClick={() => setSwitchStaffOpen(true)}
                  />
                )}
                {canCreate("Classes") && (
                  <ActionBtn
                    icon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
                    label="Create New Class"
                    onClick={() => navigate("/assign/classes/create")}
                  />
                )}
                {canCreate("Classes") && (
                  <ActionBtn
                    icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                    label="Duplicate Class"
                    onClick={() => setDuplicateOpen(true)}
                  />
                )}
                {canDeleteClass && <ActionBtn icon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />} label="Delete Class" danger onClick={() => setConfirmDelete(true)} />}
              </Box>
            </Section>

            {/* Staff details */}
            {d.staff && (
              <Section title="Staff Details" action={<Button size="small" onClick={() => navigate(`/team-history/${d.staff!.id}?return_url=${encodeURIComponent(`/class-schedules/${id}`)}`)} sx={{ textTransform: "none", fontSize: 12 }}>Profile</Button>}>
                {canEditClass && (
                  <Typography sx={{ fontSize: 11.5, color: "#64748b", mb: 1.25 }}>
                    Use Switch Staff to replace the teacher assigned to this class.
                    {hasMultiplePatterns && (
                      <> Applies to the primary schedule pattern.</>
                    )}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                  <Avatar src={d.staff.image || undefined} sx={{ width: 46, height: 46, fontSize: 18 }}>{(d.staff.name || "?").charAt(0)}</Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <StaffProfileLink id={d.staff.id} name={d.staff.name} returnUrl={`/class-schedules/${id}`} sx={{ display: "block", fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {d.staff.name}
                    </StaffProfileLink>
                    <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }} noWrap>Staff #{d.staff.id}{d.staff.email ? ` · ${d.staff.email}` : ""}</Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {/* Staff qualification matrix. The backend emits a real
                      grade × subject grouping on d.staff.grade_subjects
                      (e.g. { grade_id, grade_name, subjects: [...] }), so
                      we use GradeSubjectsSummary — same primitive the
                      class template uses — to render chips grouped by
                      grade and to open the grade-aware modal with grade
                      headings visible. Falls back to the flat primitive
                      only if the mapping payload is missing (old backend
                      build), which also preserves the pre-upgrade behaviour. */}
                  <Field
                    label="Subjects"
                    value={
                      Array.isArray((d.staff as any).grade_subjects) && (d.staff as any).grade_subjects.length > 0 ? (
                        <GradeSubjectsSummary
                          groups={(d.staff as any).grade_subjects}
                          modalTitle={`${d.staff.name || "Staff"} · Subjects`}
                          variant="hero"
                          viewButtonLabel="View Subjects"
                        />
                      ) : (
                        <FlatSubjectsSummary
                          subjects={d.staff.subjects || []}
                          modalTitle={`${d.staff.name || "Staff"} · Subjects`}
                          modalSubtitle="Subjects this staff member teaches."
                        />
                      )
                    }
                  />
                  <Field label="Locations" value={d.staff.locations?.length ? d.staff.locations.join(", ") : "—"} />
                  <Field label="Upcoming Workload" value={`${d.staff.upcoming_workload} sessions`} />
                </Box>
              </Section>
            )}

            {/* Enrollment summary */}
            <Section title="Enrollment Summary">
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Field label="Capacity" value={cap || "—"} />
                <Field label="Enrolled" value={en.enrolled} />
                <Field label="Available" value={en.available ?? (cap ? cap - en.enrolled : "—")} />
              </Box>
              <LinearProgress
                variant="determinate"
                value={fillPct}
                sx={{ height: 8, borderRadius: 999, bgcolor: "#eef2f7", "& .MuiLinearProgress-bar": { bgcolor: enrollFg, borderRadius: 999 } }}
              />
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: enrollFg }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: enrollFg }}>{en.label}</Typography>
              </Box>
            </Section>

            {/* Metadata */}
            <Section title="Class Metadata">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Field label="Class ID" value={`#${cls.id}`} />
                <Field label="Class Type" value={cls.class_type || "—"} />
                <Field label="Enrollment Type" value={cls.program_type || "—"} />
                <Field label="Created" value={fmtDateTime(cls.created_at)} />
              </Box>
            </Section>
          </Grid>
        </Grid>
      </Box>

      {/* Sticky mobile action bar */}
      <Box sx={{ display: { xs: "flex", md: "none" }, position: "fixed", bottom: 0, left: 0, right: 0, bgcolor: "#fff", borderTop: "1px solid rgba(15,23,42,0.08)", p: 1.25, gap: 1, zIndex: 20 }}>
        {canEditClass && <Button fullWidth variant="outlined" startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />} onClick={() => goEdit()} sx={{ textTransform: "none", borderRadius: "9px" }}>Edit</Button>}
        {canEditClass && <Button fullWidth variant="contained" startIcon={<PersonAddAltIcon sx={{ fontSize: 16 }} />} onClick={() => navigate(addStudentUrl)} sx={{ textTransform: "none", borderRadius: "9px" }}>Add Student</Button>}
      </Box>

      {/* Per-student kebab menu. State lives on the parent so the Menu's
          anchorEl can be the IconButton inside the mapped row without each
          row owning its own open state. */}
      <Menu
        anchorEl={studentMenu?.anchor ?? null}
        open={!!studentMenu}
        onClose={() => setStudentMenu(null)}
      >
        <MenuItem
          onClick={() => {
            const sid = studentMenu?.studentId;
            setStudentMenu(null);
            if (sid) navigate(`/student-history/${sid}`);
          }}
        >
          View Student
        </MenuItem>
        {canEditClass && scheduleId !== null && (
          <MenuItem
            onClick={() => {
              const sid = studentMenu?.studentId ?? null;
              setStudentMenu(null);
              setReleaseStudentId(sid);
              setReleaseOpen(true);
            }}
            sx={{ color: "#dc2626" }}
          >
            Release From Class
          </MenuItem>
        )}
      </Menu>

      {/* Release Student — reuses the Staff View pattern-level dialog with
          the clicked student preselected. The dialog itself owns the
          effective-date (defaults to today), reason/notes fields, and the
          "past attendance and invoiced sessions are preserved" copy. */}
      {releaseOpen && pattern && (
        <ReleaseStudentDialog
          open={releaseOpen}
          pattern={{
            ...pattern,
            // Preselect by narrowing the picker's options to just the
            // chosen student. The user still confirms the dialog to submit.
            students: releaseStudentId
              ? pattern.students.filter((s) => s.id === releaseStudentId)
              : pattern.students,
          }}
          occurrenceId={
            occurrenceId
              ? Number(occurrenceId)
              : (pattern.next_occurrence_id ?? null)
          }
          occurrenceDate={
            (d?.schedule?.occurrence_date as string | undefined)
            ?? pattern.next_occurrence_date
            ?? null
          }
          hasMultiplePatterns={hasMultiplePatterns}
          onClose={() => { setReleaseOpen(false); setReleaseStudentId(null); }}
          onDone={() => { onPatternActionDone(); setReleaseStudentId(null); }}
        />
      )}

      {/* Switch Staff — reuses the Staff View pattern-level dialog. The
          backend requires a valid replacement staff id, so this is a
          replacement flow, not a "clear assignment" flow (audit §8–§9). */}
      {switchStaffOpen && pattern && (
        <SwitchStaffDialog
          open={switchStaffOpen}
          pattern={pattern}
          onClose={() => setSwitchStaffOpen(false)}
          onDone={onPatternActionDone}
        />
      )}

      {/* Duplicate Class — real duplicate flow (POST /class-schedule/duplicate),
          not a redirect to the Create form. Default scope is single-occurrence
          per the dialog's own default; admins can switch to "Entire recurring
          class/series" inside the dialog. */}
      {duplicateOpen && (
        <DuplicateDialog
          open={duplicateOpen}
          row={duplicateRow}
          staffList={[]}
          // This page represents the recurring class/template, so default
          // the duplicate scope to "series". Other entry points keep the
          // dialog's own "occurrence" default (prop is optional).
          defaultScope="series"
          onClose={() => setDuplicateOpen(false)}
          onSuccess={() => { setDuplicateOpen(false); refetch(); }}
        />
      )}

      {/* Delete confirm */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete this class?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: "#475569" }}>
            This removes the class template. Past attendance and invoiced sessions are preserved. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDelete(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" color="error" disabled={isDeleting} onClick={handleDelete} sx={{ textTransform: "none", borderRadius: "9px" }}>{isDeleting ? "Deleting…" : "Delete Class"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ActionBtn: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean; disabled?: boolean; tip?: string }> = ({ icon, label, onClick, danger, disabled, tip }) => {
  const btn = (
    <Button
      fullWidth
      disabled={disabled}
      onClick={onClick}
      startIcon={icon}
      sx={{
        justifyContent: "flex-start", textTransform: "none", fontWeight: 600, fontSize: 13,
        borderRadius: "10px", px: 1.5, py: 1, color: danger ? "#dc2626" : "#334155",
        border: "1px solid", borderColor: danger ? "rgba(220,38,38,0.2)" : "rgba(15,23,42,0.08)",
        bgcolor: danger ? "rgba(220,38,38,0.03)" : "#fff",
        "&:hover": { bgcolor: danger ? "rgba(220,38,38,0.07)" : "#f8fafc", borderColor: danger ? "#dc2626" : "rgba(15,23,42,0.15)" },
      }}
    >
      {label}
    </Button>
  );
  return tip ? <Tooltip title={tip}><span>{btn}</span></Tooltip> : btn;
};

export default ClassDetailPage;
